import React from 'react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfoBlock } from '@picsio/ui';
import { Interweave } from 'interweave';
import _get from 'lodash/get';
import { reloadApp, reloadPage } from '../../../helpers/history';
import { showDialog, showErrorDialog } from '../../../components/dialog';
import sanitizeXSS from '../../../shared/sanitizeXSS';
import {
  showValidateCardForm,
} from '../../helpers/billing';
import { getSelectedPlan } from '../../selectors/billing';

const subscribe = createAsyncThunk(
  'billing/subscribe',
  async (
    payload,
    {
      getState, rejectWithValue, extra: {
        sdk, utils, localization, UiBlocker, Logger,
      },
    },
  ) => {
    const selectedPlan = getSelectedPlan(getState());
    const addonsFeatures = getState().user.addonsFeatures || {};

    Logger.log('User', 'SettingsBillingChangeSubscribe');
    const { plan, totalAccount, ...data } = payload;

    const selectedPlanAddons = _get(selectedPlan, `plans[${plan.interval}].addons`, []);
    const tiers = selectedPlanAddons.find(({ metadata }) => metadata.featureName === 'aiKit')?.tiers || [];

    const { addons } = totalAccount;

    const aiKit = addons.find(({ featureName }) => featureName === 'aiKit');

    const selectedTier = tiers.find(({ id }) => id === aiKit?.id) || {};

    const aiKitHasTier = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5'].includes(selectedTier?.nickname);

    const isRemovedAiKit = !aiKit?.value && addonsFeatures.aiKit?.id;

    data.addons = totalAccount.addons;
    const {
      name: planName, amount,
    } = plan;
    const { sum } = totalAccount;
    const stripePlan = {
      panelLabel: localization.BILLING.checkoutSubscribe,
      name: planName,
      amount,
    };
    const { user } = getState();
    const {
      customer: { card },
    } = user;

    try {
      UiBlocker.block(localization.BILLING.textPaymentLoadingDetails);

      if (!card) {
        const { id: tokenId } = await showValidateCardForm({
          user,
          plan: stripePlan,
          amount: sum * 100,
        });

        if (tokenId) {
          data.tokenId = tokenId;
        } else {
          throw new Error('tokenId is undefined');
        }
      }

      UiBlocker.block(localization.BILLING.textPaymentFinishing);

      const { data: subscription } = await sdk.billing.subscribe(data);

      Logger.log('User', 'SubscriptionChanged', planName);

      const userDialogueMessage = localization.BILLING.textTextSubcriptionUpdated;
      const infoBlockText = isRemovedAiKit ? localization.BILLING.textRemovalAiKit : localization.BILLING.textActivateVisualSearch;
      const shouldShowCustomDialog = aiKitHasTier || isRemovedAiKit;
      const shouldShowCancelBtn = aiKitHasTier && !isRemovedAiKit;

      const dialogData = shouldShowCustomDialog ? {
        children: (
          <div>
            <div>
              <Interweave content={sanitizeXSS(userDialogueMessage)} />
            </div>
            <br />
            <InfoBlock>{infoBlockText}</InfoBlock>
            <br />
          </div>),
      } : { text: userDialogueMessage };

      const okText = isRemovedAiKit ? localization.BILLING.textBtnOk : localization.BILLING.textBtnGoToEnableVisualSearch;
      const textBtnOk = shouldShowCustomDialog ? okText : localization.BILLING.textBtnGoToLibrary;
      const textBtnCancel = shouldShowCancelBtn ? localization.BILLING.textBtnSkipForNow : null;

      Logger.log('UI', 'ChangeSubscribeSuccessDialog', { userDialogueMessage });

      const onOk = () => {
        Logger.log('User', 'SubscriptionUpdatedChooseNextAction', {
          action_chosen: textBtnOk,
        });
        return shouldShowCancelBtn ? reloadPage('/teammates?tab=ai') : reloadApp();
      };

      const onCancel = () => {
        Logger.log('User', 'SubscriptionUpdatedChooseNextAction', {
          action_chosen: textBtnCancel,
        });
        return aiKitHasTier ? reloadApp() : reloadPage('/billing?tab=overview');
      };

      showDialog({
        title: localization.BILLING.textTitleThanks,
        textBtnCancel,
        textBtnOk,
        ...dialogData,
        onOk,
        onCancel,
      });

      UiBlocker.block(localization.BILLING.textDetailsSaving);

      const result = {
        subscription,
      };

      return result;
    } catch (err) {
      const errorSubcode = utils.getDataFromResponceError(err, 'subcode');
      const errorMessage = utils.getDataFromResponceError(err, 'msg');
      const errorReason = utils.getDataFromResponceError(err, 'reason');
      let userDialogueMessage = '';
      if (errorSubcode === 'CardError') {
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showErrorDialog(localization.BILLING.stripeCardErrors[errorReason] || localization.BILLING.stripeCardErrors.default, errorMessage);
      }
      if (errorSubcode === 'NextPlanLimitsError') {
        userDialogueMessage = localization.BILLING.DIALOG_LIMITS_EXCEEDED.text;
        const dialogData = {
          title: localization.BILLING.DIALOG_LIMITS_EXCEEDED.title,
          text: userDialogueMessage,
          textBtnCancel: localization.DIALOGS.btnOk,
          textBtnOk: null,
        };
        if (user?.subscriptionFeatures?.chatSupport) {
          dialogData.textBtnCancel = localization.DIALOGS.btnCancel;
          dialogData.textBtnOk = localization.BILLING.DIALOG_LIMITS_EXCEEDED.btnOk;
          dialogData.onOk = () => window.dispatchEvent(new Event('toolbar:ui:liveSupport'));
          dialogData.id = 'itemliveSupport';
        }
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showDialog(dialogData);
      }
      if (err.message === 'tokenId is undefined') {
        userDialogueMessage = localization.BILLING.textCardCantCheck;
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showErrorDialog(userDialogueMessage);
      }
      Logger.error(new Error('Subscribe to plan failed'), { error: err }, [
        'SubscribeBillingPlanFailed',
        { errorMessage: (err && err.message) || 'NoMessage', userDialogueMessage },
      ]);
      return rejectWithValue(err);
    } finally {
      UiBlocker.unblock();
    }
  },
);

export default subscribe;
